/**
 * SegmentSelector
 *
 * Full-screen modal that appears once per session (gated via sessionStorage).
 * Lets visitors self-identify as a founder or investor before seeing the main
 * site, so we can personalise CTAs and show the right signup form.
 *
 * Props:
 *   onSelect(segment), called with "founder" or "investor" when chosen
 *   onDismiss()      , called when user closes without selecting
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, TrendingUp, X } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Segment = "founder" | "investor";

interface SegmentSelectorProps {
  onSelect: (segment: Segment) => void;
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Storage key
// ---------------------------------------------------------------------------

const SEGMENT_KEY = "adv3_segment";

/** Returns the previously chosen segment, or null if not yet set. */
export function getSavedSegment(): Segment | null {
  try {
    const val = localStorage.getItem(SEGMENT_KEY);
    return val === "founder" || val === "investor" ? val : null;
  } catch {
    return null;
  }
}

/** Persist the chosen segment across sessions. */
export function saveSegment(segment: Segment) {
  try {
    localStorage.setItem(SEGMENT_KEY, segment);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Sub-component: Segment card
// ---------------------------------------------------------------------------

interface CardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: "primary" | "secondary";
  onClick: () => void;
}

const SegmentCard = ({ icon, title, description, accent, onClick }: CardProps) => {
  const borderColor =
    accent === "primary" ? "hover:border-primary/70" : "hover:border-secondary/70";
  const bgColor =
    accent === "primary"
      ? "hover:bg-primary/10 group-hover:bg-primary/10"
      : "hover:bg-secondary/10 group-hover:bg-secondary/10";
  const iconBg = accent === "primary" ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary";
  const ringColor = accent === "primary" ? "focus-visible:ring-primary" : "focus-visible:ring-secondary";

  return (
    <motion.button
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`group glass rounded-2xl p-8 text-left w-full transition-all duration-200 ${borderColor} ${bgColor} ${ringColor} focus-visible:outline-none focus-visible:ring-2`}
    >
      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl ${iconBg} mb-5`}>
        {icon}
      </div>
      <h3 className="text-xl font-display font-bold mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </motion.button>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SegmentSelector = ({ onSelect, onDismiss }: SegmentSelectorProps) => {
  const [visible, setVisible] = useState(false);

  // Delay appearance slightly so the page renders first
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  const handleSelect = (segment: Segment) => {
    saveSegment(segment);
    setVisible(false);
    // Small delay so exit animation plays before parent acts
    setTimeout(() => onSelect(segment), 250);
  };

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => onDismiss(), 250);
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleDismiss();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  });

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
            onClick={handleDismiss}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            key="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="segment-title"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="glass rounded-3xl p-8 sm:p-10 w-full max-w-2xl relative shadow-2xl">
              {/* Close button */}
              <button
                onClick={handleDismiss}
                aria-label="Close"
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 mb-4">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                  <span className="text-sm font-medium text-primary">Personalise your experience</span>
                </div>
                <h2 id="segment-title" className="text-2xl sm:text-3xl font-display font-bold mb-2">
                  What brings you to AdV3nture?
                </h2>
                <p className="text-muted-foreground text-sm">
                  We'll show you the most relevant opportunities for your goals.
                </p>
              </div>

              {/* Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <SegmentCard
                  icon={<Rocket className="w-7 h-7" aria-hidden="true" />}
                  title="I'm raising capital"
                  description="Discover investors aligned with your stage, sector, and geography, without cold emails."
                  accent="primary"
                  onClick={() => handleSelect("founder")}
                />
                <SegmentCard
                  icon={<TrendingUp className="w-7 h-7" aria-hidden="true" />}
                  title="I'm seeking deals"
                  description="Explore founders actively raising in your focus areas. Pre-filtered, structured, and ready to review."
                  accent="secondary"
                  onClick={() => handleSelect("investor")}
                />
              </div>

              <p className="text-center text-xs text-muted-foreground mt-6">
                You can change this at any time from your profile.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SegmentSelector;
